"use client";

import { FC, HTMLAttributes, ReactNode, useState } from "react";
import clsx from "clsx";
import { yupResolver } from "@hookform/resolvers/yup";
import axios from "axios";
import { useForm } from "react-hook-form";
import { ArrowRight, CheckCircle, Mail } from "react-feather";

import TInput from "@/components/atoms/input/TInput";
import { newsLetterSchema } from "@/app/api/validationSchemas";
import GoogleReCaptcha from "../common/GoogleReCaptcha";
import useGoogleReCaptcha from "@/hooks/useGoogleReCaptcha";
import { handleException, IAxiosError } from "@/utils/exception.helper";

// ----------------------------------------------------------------------------
// Newsletter signup — compact card with first name + last name + work email.
//
// Heading + subhead on the left, 2x2 input grid (firstName / lastName on row
// 1, email / Subscribe on row 2) on the right. Stack vertically on mobile.
// Names required so MQL routing + Sendy personalisation have real data.
// ----------------------------------------------------------------------------

type INewsLetterSubscribeSectionProps = HTMLAttributes<HTMLElement> & {
  heading?: string;
  sub_heading?: string;
  description?: ReactNode;
};

type IDefaultValues = {
  firstName: string;
  lastName: string;
  email: string;
};
const defaultValues: IDefaultValues = {
  firstName: "",
  lastName: "",
  email: "",
};

const NewsLetterSubscribeSection: FC<INewsLetterSubscribeSectionProps> = ({
  className,
  heading,
  sub_heading,
  ...other
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);

  const {
    reCaptchaRef,
    isReCaptchaAvailable,
    isRecaptchaVarified,
    resetReCaptcha,
  } = useGoogleReCaptcha();

  const { handleSubmit, reset, control } = useForm({
    defaultValues,
    resolver: yupResolver(newsLetterSchema),
  });

  const onSubmit = async (formData: IDefaultValues) => {
    const { success, token } = await isRecaptchaVarified();
    if (!success) return;
    try {
      setIsSubmitting(true);
      await axios.post("/api/newslatter/send-mail", formData, {
        headers: token ? { "x-recaptcha-token": token } : undefined,
      });
      reset();
      setIsSubscribed(true);
      // The inline card already celebrates — skip the toast to avoid
      // double-notification noise on the conversion moment.
    } catch (error) {
      handleException(error as IAxiosError);
    } finally {
      setIsSubmitting(false);
      resetReCaptcha();
    }
  };

  return (
    <section
      className={clsx("container py-8 md:py-10 lg:py-12", className)}
      {...other}
    >
      <div className="flex flex-col items-center gap-5 rounded-2xl bg-gradient-to-r from-primary-900 to-secondary-900 px-6 py-6 shadow-lg md:flex-row md:gap-8 md:px-10 md:py-7">
        {/* Left — heading + subhead */}
        <div className="flex-1 text-center md:text-left">
          <p className="mb-1.5 inline-flex items-center gap-1.5 font-bricolage-grotesque text-[11px] font-semibold uppercase tracking-[0.2em] text-yellow-400">
            <Mail size={13} />
            Friday Briefing
          </p>
          <h3 className="font-bricolage-grotesque text-[clamp(1.125rem,1vw+0.875rem,1.5rem)] font-medium !leading-[1.25] text-white">
            {heading || "Stay ahead of threats you haven't heard of yet."}
          </h3>
          {sub_heading && (
            <p className="mt-1 text-sm text-white/70">{sub_heading}</p>
          )}
        </div>

        {/* Right — form or beautiful inline success state */}
        {isSubscribed ? (
          <div className="flex w-full shrink-0 flex-col items-center gap-3 rounded-2xl border border-white/15 bg-white/[0.07] px-6 py-5 text-center backdrop-blur-sm md:w-[480px]">
            <div className="relative inline-flex h-12 w-12 items-center justify-center rounded-full bg-yellow-400 text-primary-900 shadow-[0_0_28px_rgba(250,204,21,0.45)]">
              <CheckCircle size={26} strokeWidth={2.5} />
            </div>
            <div>
              <p className="font-bricolage-grotesque text-lg font-medium text-white">
                You&apos;re on the list.
              </p>
              <p className="mt-1 text-sm text-white/75">
                First Friday briefing lands in your inbox this week.
              </p>
            </div>
          </div>
        ) : (
          <form
            onSubmit={handleSubmit(onSubmit)}
            id="news-letter-form"
            className="grid w-full shrink-0 grid-cols-1 gap-2 sm:grid-cols-2 md:w-[480px]"
          >
            <TInput
              name="firstName"
              control={control}
              placeholder="First name"
              classNames={{
                input:
                  "h-11 rounded-full !border-0 bg-white px-4 text-sm text-black-900 placeholder:text-black-400 focus:!ring-2 focus:!ring-[#FB441B]",
              }}
            />
            <TInput
              name="lastName"
              control={control}
              placeholder="Last name"
              classNames={{
                input:
                  "h-11 rounded-full !border-0 bg-white px-4 text-sm text-black-900 placeholder:text-black-400 focus:!ring-2 focus:!ring-[#FB441B]",
              }}
            />
            <TInput
              name="email"
              control={control}
              placeholder="your@company.com"
              classNames={{
                input:
                  "h-11 rounded-full !border-0 bg-white px-4 text-sm text-black-900 placeholder:text-black-400 focus:!ring-2 focus:!ring-[#FB441B]",
              }}
            />
            <button
              id="newsletter-form-submit-button"
              type="submit"
              disabled={isSubmitting}
              className={clsx(
                // Orange — matches the site-wide hero/CTA pattern
                // (CTAButton variant="orange"). Hover gets the same
                // soft orange glow shadow used by the home page CTAs.
                "group inline-flex h-11 items-center justify-center gap-2 rounded-full bg-[#FB441B] px-5 font-bricolage-grotesque text-sm font-medium text-white shadow-sm transition-all hover:bg-[#E03A17] hover:shadow-[0_10px_25px_rgba(251,68,27,0.3)]",
                isSubmitting && "!cursor-not-allowed opacity-70",
              )}
            >
              {isSubmitting ? "Subscribing…" : "Subscribe"}
              {!isSubmitting && (
                <ArrowRight
                  size={16}
                  className="transition-transform duration-300 group-hover:translate-x-0.5"
                />
              )}
            </button>
          </form>
        )}
      </div>

      {/* reCAPTCHA + microcopy below the strip — keeps the card slim */}
      {!isSubscribed && (
        <div className="mt-3 flex flex-col items-center gap-2 text-xs text-black-500 md:flex-row md:justify-center">
          {isReCaptchaAvailable && <GoogleReCaptcha ref={reCaptchaRef} />}
          <span>
            Join 2,500+ security leaders · No spam, unsubscribe anytime.
          </span>
        </div>
      )}
    </section>
  );
};

export default NewsLetterSubscribeSection;
